<template>
  <div class="stock-log-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Stock Log Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Rows
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading stock log records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Stock Log Report</span>
              <span class="date-range-text">
                <strong>{{ periodLabel }}</strong>
              </span>
            </div>
            <p v-if="filterSummary" class="filter-line mb-0 mt-1">
              {{ filterSummary }}
            </p>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 6%">S.No</th>
              <th class="text-center" style="width: 10%">Date</th>
              <th class="text-left" style="width: 18%">Product Name</th>
              <th class="text-left" style="width: 20%">Particular</th>
              <th class="text-right" style="width: 11%">Received</th>
              <th class="text-right" style="width: 11%">Issued</th>
              <th class="text-right" style="width: 11%">Balance</th>
              <th class="text-center" style="width: 13%">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in reportItems"
              :key="'sl-' + (item.logID || index) + '-' + index"
              :class="{ 'is-opening': isOpeningRow(item) }"
            >
              <td class="text-center font-weight-bold">
                {{ item.sno != null ? item.sno : index + 1 }}
              </td>
              <td class="text-center">
                {{ formatDate(item.date) }}
              </td>
              <td class="text-left font-weight-bold">
                {{ formatName(item.productName) }}
              </td>
              <td class="text-left">
                {{ item.particular || "-" }}
              </td>
              <td class="text-right qty-cell">
                {{ formatQty(item.received) }}
              </td>
              <td class="text-right qty-cell">
                {{ formatQty(item.issued) }}
              </td>
              <td class="text-right font-weight-bold qty-cell">
                {{ formatQty(item.balance, true) }}
              </td>
              <td class="text-center status-cell">
                {{ displayStatus(item) || "-" }}
              </td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="8" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No stock log records found for the selected filters</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "StockLogReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      productID: "",
      categoryID: "",
      warehouseID: "",
      supplierID: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      productName: "",
      categoryName: "",
      warehouseName: "",
      supplierName: "",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    periodLabel() {
      if (this.dateFrom && this.dateTo) {
        return `${this.formatDate(this.dateFrom)} – ${this.formatDate(
          this.dateTo
        )}`;
      }
      if (this.dateFrom) return `From ${this.formatDate(this.dateFrom)}`;
      if (this.dateTo) return `Until ${this.formatDate(this.dateTo)}`;
      return `Generated on ${this.currentDateTime}`;
    },
    filterSummary() {
      const bits = [];
      if (this.productName) bits.push(`Product: ${this.productName}`);
      if (this.categoryName) bits.push(`Category: ${this.categoryName}`);
      if (this.warehouseName) bits.push(`Warehouse: ${this.warehouseName}`);
      if (this.supplierName) bits.push(`Supplier: ${this.supplierName}`);
      if (this.status === "stock-in") bits.push("Status: Stock-In");
      if (this.status === "stock-out") bits.push("Status: Stock-Out");
      return bits.join(" · ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query || {};
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || query.cId || this.$store.state.userData.cId || "";
      this.productID = query.productID || "";
      this.categoryID = query.categoryID || "";
      this.warehouseID = query.warehouseID || "";
      this.supplierID = query.supplierID || "";
      this.status = query.status || "";
      this.dateFrom = query.dateFrom || query.dtFrom || "";
      this.dateTo = query.dateTo || query.dtTo || "";
      this.productName = query.productName || "";
      this.categoryName = query.categoryName || "";
      this.warehouseName = query.warehouseName || "";
      this.supplierName = query.supplierName || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}campuses`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name || match.organizationName || "MY SKOOL";
            this.campusBranch =
              this.cID == 0 ? "All Campuses" : match.branch || "";
            this.schoolLogo = match.logo || null;
            return;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
      }

      const currentBranch =
        (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
        this.$store.state.currentBranch;
      if (currentBranch) {
        this.campusName =
          currentBranch.organizationName ||
          currentBranch.name ||
          currentBranch.branch ||
          "MY SKOOL";
        this.campusBranch = currentBranch.branch || "";
        this.schoolLogo = currentBranch.logo || null;
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          db: this.db,
          cID: this.cID,
        };
        if (this.productID) params.productID = this.productID;
        if (this.categoryID) params.categoryID = this.categoryID;
        if (this.warehouseID) params.warehouseID = this.warehouseID;
        if (this.supplierID) params.supplierID = this.supplierID;
        if (this.status) params.status = this.status;
        if (this.dateFrom) params.dateFrom = this.dateFrom;
        if (this.dateTo) params.dateTo = this.dateTo;

        const response = await axios.get(
          `${this.$store.state.domain}posStock/StockLogReport`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data || {};
        if (payload.status && payload.status !== "success") {
          this.error = payload.message || "Failed to load stock log report.";
          this.reportItems = [];
          return;
        }

        if (payload.status === "success") {
          this.reportItems = Array.isArray(payload.data) ? payload.data : [];
        } else if (Array.isArray(payload)) {
          this.reportItems = payload;
        } else if (Array.isArray(payload.data)) {
          this.reportItems = payload.data;
        } else {
          this.error =
            payload.message ||
            "The server returned an unexpected response format.";
          this.reportItems = [];
        }
      } catch (err) {
        console.error("StockLogReport error:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
        this.reportItems = [];
      } finally {
        this.loading = false;
      }
    },
    isOpeningRow(item) {
      const raw = String((item && item.statusRaw) || "").toLowerCase();
      const particular = String((item && item.particular) || "").toLowerCase();
      return raw === "opening" || particular.includes("opening balance");
    },
    displayStatus(item) {
      const label = String((item && item.status) || "").trim();
      if (label) return label;

      const raw = String((item && item.statusRaw) || "")
        .toLowerCase()
        .replace(/\s+/g, "-");
      if (raw === "stock-in" || raw === "stockin") return "Stock-In";
      if (raw === "stock-out" || raw === "stockout") return "Stock-Out";
      if (raw === "opening") return "Opening";
      return "";
    },
    formatName(value) {
      const text = String(value || "").trim();
      return text ? text.toUpperCase() : "-";
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    formatQty(val, keepZero = false) {
      if (val === null || val === undefined || val === "") {
        return keepZero ? "0" : "-";
      }
      const n = Number(val);
      if (!Number.isFinite(n)) return String(val);
      if (!keepZero && n === 0) return "-";
      return new Intl.NumberFormat("en-PK", {
        maximumFractionDigits: 2,
      }).format(n);
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.stock-log-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }

  .filter-line {
    font-size: 0.9rem;
    font-weight: 600;
    color: #636e72;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
    vertical-align: middle;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  tbody tr.is-opening td {
    background: #f1f4f8 !important;
    font-weight: 700;
  }

  .qty-cell {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .status-cell {
    font-weight: 700;
    white-space: nowrap;
  }
}

.no-data {
  color: #636e72;
  font-weight: 600;
}

@page {
  size: A4 landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .stock-log-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead {
      display: table-header-group;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr {
      page-break-inside: avoid;
    }

    tbody tr.is-opening td {
      background: #f1f2f6 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.8rem !important;
    }
  }
}
</style>
